import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useWebSocket } from '../hooks/useWebSocket';
import { mockAgents } from '../mockData';
import api from '../utils/api';
import ChatMessage from './chat/ChatMessage';
import StreamingMessage from './chat/StreamingMessage';
import TypingIndicator from './chat/TypingIndicator';
import ChatInput from './chat/ChatInput';
import ChatHeader from './chat/ChatHeader';
import BuildProgressBar from './chat/BuildProgressBar';
import TeamPresenceBar from './team/TeamPresenceBar';
import ChatQuickStart from './chat/ChatQuickStart';

const ChatInterface = ({ project }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingAgent, setStreamingAgent] = useState(null);
  const [buildProgress, setBuildProgress] = useState(null);
  const [creditDelta, setCreditDelta] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishUrl, setPublishUrl] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const messagesEndRef = useRef(null);
  const { user, refreshUser, setUser } = useAuth();
  const { connected, on, emit } = useWebSocket(project?.id);

  useEffect(() => {
    const loadMessages = async () => {
      if (!project?.id) return;
      try {
        const response = await api.get(`/projects/${project.id}/messages`);
        setMessages(response.data || []);
      } catch { setMessages([]); }
    };
    loadMessages();
  }, [project?.id]);

  useEffect(() => {
    on('message', (data) => {
      if (data.role === 'user') {
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== `optimistic-${data.content?.slice(0, 10)}`);
          return [...filtered, { ...data, timestamp: data.timestamp || new Date().toISOString() }];
        });
      }
    });

    on('build_progress', (data) => {
      setBuildProgress({ stage: data.stage, label: data.label, progress: data.progress, agentType: data.agentType });
    });

    on('stream_start', (data) => {
      setStreamingAgent(data.agentType);
      setStreamingContent('');
      setIsStreaming(true);
      setBuildProgress({ stage: 'streaming', label: 'Generating response...', progress: data.progress });
    });

    on('stream_chunk', (data) => { setStreamingContent(data.fullContent); });

    on('stream_end', (data) => {
      setIsStreaming(false);
      setStreamingContent('');
      setStreamingAgent(null);
      setBuildProgress(null);
      setMessages(prev => [...prev, {
        id: data.messageId, role: 'assistant', content: data.content,
        agentType: data.agentType, agentStatus: 'awaiting_input',
        timestamp: data.timestamp, selectedModel: data.selectedModel
      }]);
      if (data.credits !== undefined) {
        const prevCredits = user?.credits || 0;
        const delta = data.credits - prevCredits;
        if (delta < 0) { setCreditDelta(delta); setTimeout(() => setCreditDelta(null), 2000); }
        setUser(prev => prev ? { ...prev, credits: data.credits } : prev);
      }
    });

    on('error', () => { setIsStreaming(false); setBuildProgress(null); });
    on('connected', (data) => { if (data.members) setTeamMembers(data.members); });
    on('presence', (data) => { if (data.members) setTeamMembers(data.members); });
    on('team_activity', (data) => {
      setTeamMembers(prev => prev.map(m => m.id === data.user_id ? { ...m, activity: data.activity } : m));
    });
  }, [on, user?.credits, setUser]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamingContent]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming) return;
    const userContent = input.trim();
    setInput('');

    if (connected) {
      setMessages(prev => [...prev, { id: `user-${Date.now()}`, role: 'user', content: userContent, timestamp: new Date().toISOString() }]);
      emit({ type: 'chat', content: userContent });
      setIsStreaming(true);
      setBuildProgress({ stage: 'sending', label: 'Sending...', progress: 5 });
    } else {
      const optimisticMsg = { id: `temp-${Date.now()}`, role: 'user', content: userContent, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, optimisticMsg]);
      setIsStreaming(true);
      try {
        const response = await api.post(`/projects/${project.id}/messages`, { content: userContent, role: 'user' });
        const { aiResponse } = response.data;
        setMessages(prev => { const filtered = prev.filter(m => m.id !== optimisticMsg.id); return [...filtered, response.data.message, aiResponse]; });
        const prevCredits = user?.credits || 0;
        const updatedUser = await refreshUser();
        if (updatedUser) { const delta = (updatedUser.credits || 0) - prevCredits; if (delta < 0) { setCreditDelta(delta); setTimeout(() => setCreditDelta(null), 2000); } }
      } catch {
        setMessages(prev => [...prev, { id: `ai-${Date.now()}`, role: 'assistant', content: "I'm having trouble connecting. Please try again.", timestamp: new Date().toISOString(), agentType: 'coordinator' }]);
      } finally { setIsStreaming(false); }
    }
  }, [input, isStreaming, connected, emit, project?.id, user?.credits, refreshUser]);

  const handlePublishToWeb = async () => {
    if (!project?.id) return;
    setPublishing(true);
    setPublishUrl(null);
    try {
      const codeBlocks = [];
      const codeRegex = /```(\w+)?\n([\s\S]*?)```/g;
      messages.filter(m => m.role === 'assistant').forEach(msg => {
        let match;
        while ((match = codeRegex.exec(msg.content || '')) !== null) {
          codeBlocks.push({ language: match[1] || 'text', content: match[2].trim() });
        }
      });
      const files = [];
      let hasHtml = false;
      codeBlocks.forEach((block, i) => {
        const lang = (block.language || '').toLowerCase();
        if (['html', 'htm'].includes(lang) || block.content.includes('<!DOCTYPE') || block.content.includes('<html')) {
          files.push({ file: hasHtml ? `page_${i}.html` : 'index.html', data: btoa(unescape(encodeURIComponent(block.content))) });
          hasHtml = true;
        } else if (['css'].includes(lang)) {
          files.push({ file: `style${i > 0 ? `_${i}` : ''}.css`, data: btoa(unescape(encodeURIComponent(block.content))) });
        } else if (['js', 'javascript'].includes(lang)) {
          files.push({ file: `script${i > 0 ? `_${i}` : ''}.js`, data: btoa(unescape(encodeURIComponent(block.content))) });
        }
      });
      const res = await api.post('/deploy/vercel/quick', {
        project_id: project.id, project_name: project.name || 'SuperTool App',
        files: files.length > 0 ? files : undefined
      });
      if (res.data.success) setPublishUrl(res.data.url);
    } catch (err) { console.error('Publish failed:', err); }
    finally { setPublishing(false); }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Compact header — model + status + credits */}
      <ChatHeader
        modelId={project?.aiModel}
        connected={connected}
        credits={user?.credits}
        creditDelta={creditDelta}
      />

      {/* Team presence — only shows when >1 member */}
      <TeamPresenceBar members={teamMembers} />

      {/* Build progress — only shows during generation */}
      <BuildProgressBar buildProgress={buildProgress} />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        {messages.length <= 1 && !isStreaming && (
          <ChatQuickStart />
        )}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} userName={user?.name} isAuto={project?.aiModel === 'auto'} projectId={project?.id} projectName={project?.name} />
        ))}
        {isStreaming && streamingContent && <StreamingMessage streamingContent={streamingContent} streamingAgent={streamingAgent} />}
        {isStreaming && !streamingContent && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput
        input={input} setInput={setInput} onSend={handleSend} onPublish={handlePublishToWeb}
        isStreaming={isStreaming} publishing={publishing} publishUrl={publishUrl}
        connected={connected} hasMessages={messages.length > 0}
      />
    </div>
  );
};

export default ChatInterface;
